import { useSelector } from "react-redux";
import { Link, useLocation } from "react-router-dom";
import MuiBreadcrumbs from "@mui/material/Breadcrumbs";
import MenuIcon from "@mui/icons-material/Menu";
import EdgeLogo from "components/Design/EdgeLogo/EdgeLogo";
import { getBreadcrumbLabels } from "store/selectors/breadcrumbs";
import { HIRE_ROUTES } from "plg/routes";
import PlgAccountMenu from "plg/shell/PlgAccountMenu";
import MeetingDoor from "plg/shell/MeetingDoor";
import { UNLOCKS, isUnlockKey } from "plg/screens/Unlocks/unlocks";
import { useShortlist } from "components/Screens/Marketplace/Customer/Shortlist/ShortlistContext";
import "components/main/navbar.scss";

const LABELS: Record<string, string> = {
  [HIRE_ROUTES.company]: "Company",
  [HIRE_ROUTES.talentNetwork]: "Browse",
  [HIRE_ROUTES.shortlist]: "Shortlist",
  [HIRE_ROUTES.requestAHire]: "Request a Hire",
  [HIRE_ROUTES.interviewStatus]: "Interview status",
  [HIRE_ROUTES.feedback]: "Feedback",
};

// The shortlist counter beside Request a meeting (the primary action), on every portal screen (prototype header).
// A plain count, nothing at all when the shortlist is empty.
function ShortlistCounter() {
  const { count, cap } = useShortlist();
  // No limit (cap null): just "N shortlisted", no slots, never "ready".
  if (cap === null) {
    if (!count) return null;
    return (
      <Link to={HIRE_ROUTES.shortlist} className="plg-hc" aria-label={`${count} shortlisted`}>
        <span className="plg-hc__full">
          <b>{count}</b> <span className="plg-hc__word">shortlisted</span>
        </span>
        <b className="plg-hc__n" aria-hidden="true">
          {count}
        </b>
      </Link>
    );
  }
  const n = Math.min(count, cap);
  const ready = n >= cap;
  return (
    <Link
      to={HIRE_ROUTES.shortlist}
      className="plg-hc"
      aria-label={ready ? "Shortlist ready" : `${n} of ${cap} shortlisted`}
    >
      <span className="plg-hc__slots" aria-hidden="true">
        {Array.from({ length: cap }, (_, i) => (
          // eslint-disable-next-line react/no-array-index-key
          <span key={i} className={i < n ? "on" : ""} />
        ))}
      </span>
      {ready ? (
        <span className="plg-hc__full">
          <b>Shortlist ready</b>
        </span>
      ) : (
        <span className="plg-hc__full">
          <b>
            {n} of {cap}
          </b>{" "}
          <span className="plg-hc__word">shortlisted</span>
        </span>
      )}
      {/* Phones: the number only, so Request a meeting keeps its label. */}
      <b className="plg-hc__n" aria-hidden="true">
        {n}
      </b>
    </Link>
  );
}

/** `onMenu` opens the phone menu (PlgMobileNav); its button only shows below 600px, where the sidebar is hidden. */
function PlgTopBar({ open, onMenu }: { open: boolean; onMenu?: () => void }) {
  const { pathname } = useLocation();
  const dynamicLabels = (useSelector(getBreadcrumbLabels) || {}) as Record<string, string>;
  // Prototype renderHeader: no crumbs on Home; elsewhere Home / page.
  const crumbs: { to: string; label: string }[] = pathname === HIRE_ROUTES.home ? [] : [{ to: HIRE_ROUTES.home, label: "Home" }];
  if (pathname.startsWith(`${HIRE_ROUTES.talentNetwork}/`)) {
    crumbs.push({ to: HIRE_ROUTES.talentNetwork, label: "Browse" }, { to: pathname, label: dynamicLabels[pathname] || "Profile" });
  } else if (pathname.startsWith("/hire/unlocks/")) {
    const key = pathname.split("/").pop();
    crumbs.push({ to: pathname, label: isUnlockKey(key) ? UNLOCKS[key].label : "After you hire" });
  } else if (LABELS[pathname]) {
    crumbs.push({ to: pathname, label: LABELS[pathname] });
  }

  return (
    <header className="navbar-header plg-header">
      <div className="flex justify-end items-center h-full">
        <div className="plg-hdr-phone">
          <button type="button" className="plg-hdr-menu" aria-label="Open menu" onClick={onMenu}>
            <MenuIcon />
          </button>
          <Link to={HIRE_ROUTES.home} className="plg-hdr-logo" aria-label="Edge home">
            <EdgeLogo size="md" icon />
          </Link>
        </div>
        <div className={`navbar-breadcrumbs-container sidebar-toggle ${open ? "sidebar-open" : "sidebar-closed"}`}>
          <MuiBreadcrumbs aria-label="breadcrumb">
            {crumbs.map((c, i) =>
              i === crumbs.length - 1 ? (
                <span key={c.to} className="plg-crumb plg-crumb--cur">{c.label}</span>
              ) : (
                <Link key={c.to} to={c.to} className="plg-crumb">{c.label}</Link>
              )
            )}
          </MuiBreadcrumbs>
        </div>
        <div className="plg-hdr-right">
          <MeetingDoor className="plg-hcall" entry="header" />
          <ShortlistCounter />
        </div>
        <PlgAccountMenu />
      </div>
    </header>
  );
}

export default PlgTopBar;
